import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle as AlertTriangle, FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight, FiRefreshCw as RefreshCw, FiSearch as Search, FiX as X, FiTrash2 as TrashIcon, FiSquare as Square, FiCheckSquare as CheckSquare } from 'react-icons/fi';
import { taskExecutionsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import CodeSnippetModal from '../../../components/common/CodeSnippetModal';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'started', label: 'Started' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
  { value: 'retry', label: 'Retry' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'pending', label: 'Pending' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created time' },
  { value: 'started_at', label: 'Started time' },
  { value: 'finished_at', label: 'Finished time' },
  { value: 'duration_ms', label: 'Duration' },
  { value: 'retries', label: 'Retries' },
];

interface TaskExecution {
  id: string;
  task_name: string;
  task_id: string;
  status: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number | null;
  retries?: number;
  queue?: string;
  worker_hostname?: string;
  exception_message?: string;
  exception_type?: string;
  traceback?: string;
}

interface TaskSummary {
  counts_by_status?: Record<string, number>;
}

interface ErrorModalState {
  open: boolean;
  title: string;
  subtitle: string | null;
  code: string;
}

const formatDateTime = (value: string | undefined | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const formatDuration = (ms: number | null | undefined): string => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = (ms / 1000).toFixed(2);
  return `${s} s`;
};

const statusBadge = (status: string | undefined): string => {
  const s = String(status || '').toLowerCase();
  if (s === 'success') return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
  if (s === 'failure') return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
  if (s === 'retry') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
  if (s === 'started') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
};

const TaskExecutionsPage: React.FC = () => {
  useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const [executions, setExecutions] = useState<TaskExecution[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [status, setStatus] = useState('');
  const [taskNamePrefix, setTaskNamePrefix] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [selectedExecution, setSelectedExecution] = useState<TaskExecution | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [errorModal, setErrorModal] = useState<ErrorModalState>({ open: false, title: '', subtitle: null, code: '' });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const canView = useMemo(() => {
    return (
      isAdmin ||
      hasPermission(PERMISSIONS.SYSTEM_ADMIN) ||
      hasPermission(PERMISSIONS.PLATFORMOPS_MANAGE)
    );
  }, [hasPermission, isAdmin]);

  const pageInfo = useMemo(() => {
    const page = Math.floor(offset / limit) + 1;
    const totalPages = Math.max(1, Math.ceil((total || 0) / limit));
    return { page, totalPages };
  }, [offset, limit, total]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const result = await taskExecutionsAPI.listExecutions({
        limit,
        offset,
        status: status || undefined,
        taskNamePrefix: taskNamePrefix || undefined,
        sortBy,
        sortOrder,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to load task executions');
      }
      setExecutions(result.executions || []);
      setTotal(result.total || 0);
      setLimit(result.limit || limit);
      setOffset(result.offset || offset);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [canView, limit, offset, status, taskNamePrefix, sortBy, sortOrder]);

  const loadSummary = useCallback(async () => {
    if (!canView) return;
    setSummaryError(null);
    try {
      const result = await taskExecutionsAPI.getSummary({
        windowHours: 24,
        status: status || undefined,
        taskNamePrefix: taskNamePrefix || undefined,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load summary');
      setSummary(result.data);
    } catch (e: unknown) {
      setSummaryError(e instanceof Error ? e.message : String(e));
    }
  }, [canView, status, taskNamePrefix]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const openDetails = useCallback(async (executionId: string) => {
    setSelectedExecutionId(executionId);
    setSelectedExecution(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const result = await taskExecutionsAPI.getExecution(executionId);
      if (!result.success) throw new Error(result.error || 'Failed to load execution details');
      setSelectedExecution(result.data);
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetails = () => {
    setSelectedExecutionId(null);
    setSelectedExecution(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const openErrorModal = (e: TaskExecution) => {
    let exceptionPrefix: string;
    if (e?.exception_type) {
      exceptionPrefix = e.exception_type + ': ';
    } else {
      exceptionPrefix = '';
    }
    const code = e?.traceback || (e?.exception_message ? `${exceptionPrefix}${e.exception_message}` : '');
    setErrorModal({
      open: true,
      title: 'Error / Traceback',
      subtitle: e?.task_name ? `${e.task_name} • ${e.task_id || ''}` : (e?.task_id || null),
      code: code || 'No traceback captured for this run.',
    });
  };

  const onPrev = () => setOffset((prev) => Math.max(0, prev - limit));
  const onNext = () => {
    const nextOffset = offset + limit;
    if (nextOffset < total) setOffset(nextOffset);
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => prev.size === executions.length ? new Set() : new Set(executions.map((e) => e.id)));
  }, [executions]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await taskExecutionsAPI.bulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
      load();
      loadSummary();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete selected executions');
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, load, loadSummary]);

  const onApplyFilters = () => {
    setOffset(0);
    setSelectedIds(new Set());
    load();
    loadSummary();
  };

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
            <p className="text-yellow-800 dark:text-yellow-300">Access denied.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Task Executions</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Inspect Celery task runs, failures, and retries.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div className="sm:col-span-2">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Task name prefix
          </span>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            <input
              value={taskNamePrefix}
              onChange={(e) => setTaskNamePrefix(e.target.value)}
              placeholder="e.g. tasks.guardrails."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sort" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Sort
          </label>
          <select id="sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Order
          </label>
          <select id="order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
        </div>
      </div>

      {/* Summary */}
      <div className="mb-6">
        {summaryError && (
          <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-300">
            Summary unavailable: {summaryError}
          </div>
        )}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'failure', label: 'Failures', tone: 'red' },
              { key: 'retry', label: 'Retries', tone: 'yellow' },
              { key: 'started', label: 'Running', tone: 'blue' },
              { key: 'success', label: 'Success', tone: 'green' },
            ].map((c) => {
              const val = summary.counts_by_status?.[c.key] ?? 0;
              let tone: string;
              if (c.tone === 'red') {
                tone = 'border-red-200 dark:border-red-800';
              } else if (c.tone === 'yellow') {
                tone = 'border-yellow-200 dark:border-yellow-800';
              } else if (c.tone === 'blue') {
                tone = 'border-blue-200 dark:border-blue-800';
              } else {
                tone = 'border-green-200 dark:border-green-800';
              }
              return (
                <div key={c.key} className={`bg-white dark:bg-gray-800 border ${tone} rounded-lg p-4`}>
                  <div className="text-xs text-gray-600 dark:text-gray-400">Last 24h</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{val}</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">{c.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={toggleSelectAll}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title={selectedIds.size === executions.length && executions.length > 0 ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === executions.length && executions.length > 0 ? (
              <CheckSquare className="w-5 h-5" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-medium">{Math.min(total, offset + 1)}</span>{/*
            */}–{/*
            */}<span className="font-medium">{Math.min(total, offset + executions.length)}</span> of{' '}
            <span className="font-medium">{total}</span>
          </div>
        </div>
        <button
          onClick={onApplyFilters}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Apply
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {selectedIds.size} selected
          </span>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <TrashIcon className="w-4 h-4" />
            {bulkDeleting ? 'Deleting...' : 'Delete'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-4 py-3 w-10">
                  <button
                    onClick={toggleSelectAll}
                    className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    {selectedIds.size === executions.length && executions.length > 0 ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Task</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Retries</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Queue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Worker</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {executions.map((e) => (
                <tr
                  key={e.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-900/30 cursor-pointer"
                  onClick={() => openDetails(e.id)} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') openDetails(e.id); }} role="row" tabIndex={0}
                >
                  <td className="px-4 py-3 w-10" onClick={(evt) => evt.stopPropagation()} onKeyDown={(evt) => { if (evt.key === 'Enter' || evt.key === ' ') evt.stopPropagation(); }} role="presentation">
                    <button
                      onClick={() => toggleSelect(e.id)}
                      className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    >
                      {selectedIds.has(e.id) ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {formatDateTime(e.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    <div className="font-medium">{e.task_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{e.task_id}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(e.status)}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {formatDuration(e.duration_ms)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {e.retries ?? 0}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {e.queue || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {e.worker_hostname || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {e.exception_message ? (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-red-700 dark:text-red-300 line-clamp-2">{e.exception_message}</span>
                        <button
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                          onClick={(evt) => {
                            evt.stopPropagation();
                            openErrorModal(e);
                          }}
                        >
                          View
                        </button>
                      </div>
                    ) : '—'}
                  </td>
                </tr>
              ))}
              {executions.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-600 dark:text-gray-400">
                    {loading ? 'Loading…' : 'No task executions found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page <span className="font-medium">{pageInfo.page}</span> of{' '}
            <span className="font-medium">{pageInfo.totalPages}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={offset === 0}
              className="inline-flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <ChevronLeft className="w-4 h-4" />
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={offset + limit >= total}
              className="inline-flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Details modal */}
      {selectedExecutionId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeDetails} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') closeDetails(); }} role="presentation">
          <div
            className="w-full max-w-4xl bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
          
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Execution Details</h2>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{selectedExecutionId}</p>
              </div>
              <button
                onClick={closeDetails}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {detailError && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                    <p className="text-red-800 dark:text-red-300 text-sm">{detailError}</p>
                  </div>
                </div>
              )}

              {detailLoading && (
                <div className="text-sm text-gray-600 dark:text-gray-400">Loading…</div>
              )}

              {selectedExecution && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Task:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{selectedExecution.task_name}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Status:</span>{' '}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(selectedExecution.status)}`}>
                        {selectedExecution.status}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Queue:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{selectedExecution.queue || '—'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Worker:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{selectedExecution.worker_hostname || '—'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Started:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{formatDateTime(selectedExecution.started_at)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Finished:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{formatDateTime(selectedExecution.finished_at)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Duration:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{formatDuration(selectedExecution.duration_ms)}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-200">Retries:</span>{' '}
                      <span className="text-gray-900 dark:text-white">{selectedExecution.retries ?? 0}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {selectedExecution.exception_message && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Exception</div>
                        <div className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
                          {selectedExecution.exception_type ? `${selectedExecution.exception_type}: ` : ''}
                          {selectedExecution.exception_message}
                        </div>
                      </div>
                    )}

                    {selectedExecution.traceback && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Traceback</div>
                        <pre className="text-xs bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                          {selectedExecution.traceback}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <CodeSnippetModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, title: '', subtitle: null, code: '' })}
        title={errorModal.title}
        subtitle={errorModal.subtitle}
        language="python"
        code={errorModal.code}
      />
    </div>
  );
};

export default TaskExecutionsPage;

